import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { stripeApi, type Purchase } from "../lib/api";
import { ArrowLeftIcon } from "../components/ui/Icons";

export function PurchasesPage() {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await stripeApi.getPurchases();
        setPurchases(data);
      } catch (err) {
        console.error("Failed to load purchases:", err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-base">
      <Header />
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute -left-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
        <div className="absolute inset-0 bg-grid opacity-20" />
      </div>

      <main className="relative pt-24">
        <div className="mx-auto max-w-5xl px-4 pb-20 sm:px-6 lg:px-8">
          <Link
            to="/dashboard"
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-secondary"
          >
            <ArrowLeftIcon size={16} />
            Back to Dashboard
          </Link>

          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold tracking-tight text-primary">
              My <span className="text-amber">Purchases</span>
            </h1>
            <p className="mt-2 text-muted">
              Plugins you've purchased — download and install anytime
            </p>
          </div>

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card-glow animate-pulse p-6">
                  <div className="mb-3 h-5 w-40 rounded bg-elevated/50" />
                  <div className="mb-4 h-3 w-full rounded bg-elevated/50" />
                  <div className="h-4 w-20 rounded bg-elevated/50" />
                </div>
              ))}
            </div>
          ) : purchases.length === 0 ? (
            <div className="card-glow flex flex-col items-center justify-center py-20 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-elevated/50">
                <svg
                  className="h-7 w-7 text-muted"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 font-display text-lg font-bold text-primary">No purchases yet</h3>
              <p className="mb-6 max-w-sm text-sm text-muted">
                Browse the marketplace to discover premium plugins from the community.
              </p>
              <Link to="/" className="btn-primary">
                Browse Plugins
              </Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {purchases.map((purchase) => (
                <div key={purchase.id} className="card-glow p-6">
                  <div className="mb-2 flex items-start justify-between">
                    <h3 className="min-w-0 flex-1 truncate font-display text-base font-bold text-primary">
                      {purchase.plugin.name}
                    </h3>
                    <span className="ml-2 flex-shrink-0 rounded-full bg-success/10 px-2 py-0.5 font-mono text-xs text-success">
                      {formatPrice(purchase.amount)}
                    </span>
                  </div>

                  <p className="mb-4 line-clamp-2 text-sm text-muted">
                    {purchase.plugin.shortDesc || purchase.plugin.description}
                  </p>

                  <p className="mb-4 font-mono text-xs text-muted">
                    Purchased {new Date(purchase.createdAt).toLocaleDateString()}
                  </p>

                  <Link
                    to={`/plugin/${purchase.plugin.slug}`}
                    className="btn-primary block py-2 text-center text-sm"
                  >
                    View & Install
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
